import { useNavigate } from "react-router-dom";
import type { VistoriaProjeto } from "@/mocks/projetos";
import { estadoFitoConfig, riscoConfig } from "@/mocks/cta";

interface Props {
  vistorias: VistoriaProjeto[];
  concluidas: number;
  pendentes: number;
  emRevisao: number;
  vencidas: number;
}

const statusVistoriaConfig: Record<string, { badge: string; label: string }> = {
  "concluída": { badge: "bg-primary-100 text-primary-700", label: "Concluída" },
  "em revisão": { badge: "bg-warning-100 text-warning-700", label: "Em Revisão" },
  "pendente": { badge: "bg-background-100 text-foreground-500", label: "Pendente" },
  "vencida": { badge: "bg-danger-100 text-danger-700", label: "Vencida" },
};

export default function ProjectInspectionTable({ vistorias, concluidas, pendentes, emRevisao, vencidas }: Props) {
  const navigate = useNavigate();

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-4 gap-3">
        <div className="bg-primary-50 rounded-lg p-3 text-center">
          <p className="text-lg font-bold text-primary-700 font-heading tabular-nums">{concluidas}</p>
          <p className="text-xs text-primary-600 font-medium">Concluídas</p>
        </div>
        <div className="bg-warning-50 rounded-lg p-3 text-center">
          <p className="text-lg font-bold text-warning-700 font-heading tabular-nums">{pendentes}</p>
          <p className="text-xs text-warning-600 font-medium">Pendentes</p>
        </div>
        <div className="bg-caution-50 rounded-lg p-3 text-center">
          <p className="text-lg font-bold text-caution-700 font-heading tabular-nums">{emRevisao}</p>
          <p className="text-xs text-caution-600 font-medium">Em Revisão</p>
        </div>
        <div className="bg-danger-50 rounded-lg p-3 text-center">
          <p className="text-lg font-bold text-danger-700 font-heading tabular-nums">{vencidas}</p>
          <p className="text-xs text-danger-600 font-medium">Vencidas</p>
        </div>
      </div>

      <div className="overflow-x-auto border border-background-200 rounded-lg">
        <table className="w-full">
          <thead>
            <tr className="border-b border-background-200 bg-background-50">
              <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Código CTA</th>
              <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Data</th>
              <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Tipo</th>
              <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Técnico</th>
              <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Estado</th>
              <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Risco</th>
              <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Recomendação</th>
              <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Status</th>
              <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Ações</th>
            </tr>
          </thead>
          <tbody>
            {vistorias.map((v) => {
              const fito = estadoFitoConfig[v.estado] || { badge: "bg-background-100 text-foreground-500", label: v.estado };
              const rsk = riscoConfig[v.risco] || { badge: "bg-background-100 text-foreground-500", label: v.risco };
              const st = statusVistoriaConfig[v.status] || { badge: "bg-background-100 text-foreground-500", label: v.status };
              return (
                <tr key={v.id} className="border-b border-background-200 hover:bg-background-50 transition-colors">
                  <td className="px-4 py-3">
                    <span className="text-xs font-mono text-foreground-800 whitespace-nowrap">{v.codigoCta}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-foreground-700 whitespace-nowrap">{new Date(v.data).toLocaleDateString("pt-BR")}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs text-foreground-600 bg-background-100 px-2 py-0.5 rounded-md whitespace-nowrap">{v.tipo}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-foreground-700 whitespace-nowrap">{v.tecnico}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`badge ${fito.badge}`}>{fito.label}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`badge ${rsk.badge}`}>{rsk.label}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs text-foreground-600 max-w-[140px] truncate">{v.recomendacao}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`badge ${st.badge}`}>{st.label}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => navigate(`/cta/${v.codigoCta.split("-").pop()}`)}
                        className="w-7 h-7 rounded-md bg-primary-50 text-primary-600 flex items-center justify-center hover:bg-primary-100 transition-colors cursor-pointer"
                        title="Ver FCA"
                      >
                        <i className="ri-eye-line text-xs"></i>
                      </button>
                      <button className="w-7 h-7 rounded-md bg-secondary-50 text-secondary-600 flex items-center justify-center hover:bg-secondary-100 transition-colors cursor-pointer" title="Editar">
                        <i className="ri-edit-line text-xs"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <button className="btn-primary text-xs px-3 py-2 cursor-pointer">
        <i className="ri-add-line"></i>
        Nova Vistoria
      </button>
    </div>
  );
}